import { Box, Typography } from '@mui/material';
import { Plus, RefreshCw } from 'lucide-react';
import { useCallback, useState, type ReactElement } from 'react';

import { roleSatisfies, type AdminApi, type AdminPrivacyRequest, type PrivacyRequestStatus } from '@/admin/adminApi';
import { useOperationsRefresh } from '@/admin/operations/useOperationsRefresh';
import { ReadBlock } from '@/admin/pilotage/ReadBlock';
import { useAdminRead } from '@/admin/pilotage/useAdminRead';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { AccessDenied, Button, Card, EmptyState, PageHeader, SelectField } from '@/ui/kit';
import { useNavBadges } from '@/ui/shell/navBadges';

import { AccessRegistryCard } from './AccessRegistryCard';
import { EditRequestDialog } from './EditRequestDialog';
import { ExportsCard } from './ExportsCard';
import { InfoNote } from './InfoNote';
import { NewRequestDialog } from './NewRequestDialog';
import { RequestsTable } from './RequestsTable';
import { SummaryTiles } from './SummaryTiles';
import { STATUS_LABELS, formatDay, pageCaption } from './privacyRequestsModel';

/**
 * Demandes RGPD (`/compliance/privacy-requests`, BO-22, maquette P12).
 *
 * **Un registre tenu a la main.** Aucune demande n'y arrive toute seule — une
 * demande recue par courriel n'est enregistree nulle part ailleurs — et il ne
 * remplace pas le processus de reponse : il calcule l'echeance legale d'un mois,
 * garde qui suit la demande, et mesure les delais. La phrase est **toujours**
 * visible, et le registre vide dit qu'**aucun historique n'est reconstitue**.
 *
 * Quatre regles :
 * 1. **Chaque bloc charge et echoue seul** (tuiles, tableau, registre des acces,
 *    exports) : une route lente ne prive pas l'ecran du reste.
 * 2. **Aucun chiffre n'est calcule ici** : effectifs, retards, jours restants,
 *    delai moyen sont ceux du serveur. « — » quand ils manquent.
 * 3. **`support` lit, `admin` ecrit** : sans le role, les boutons d'ecriture sont
 *    absents et l'ecran dit pourquoi — un bouton grise se lirait comme une panne.
 * 4. **Aucune action lourde ici** : export et anonymisation restent sur la fiche
 *    du compte, derriere le dispositif sensible. Et l'export n'est jamais presente
 *    comme une reponse complete (#527).
 *
 * Pas de rafraichissement automatique (ADR 0035 §4) : « Actualiser », et la
 * relecture au retour sur l'onglet.
 */

const PER_PAGE = 25;

const readSummary = (api: AdminApi) => api.privacyRequestsSummary();

type OpenDialog = { readonly kind: 'new' } | { readonly kind: 'edit'; readonly request: AdminPrivacyRequest } | null;

/**
 * « Tous » porte une valeur **non vide** : MUI n'affiche pas le libelle d'une option
 * dont la valeur est `''` (il la prend pour « rien de choisi »), et le filtre
 * paraitrait vide au repos.
 */
const ALL = 'all';

const STATUS_FILTER_OPTIONS = [
  { value: ALL, label: 'Tous les statuts' },
  ...(['received', 'in_progress', 'closed'] as const).map((value) => ({ value, label: STATUS_LABELS[value] })),
];

export const PrivacyRequestsScreen = (): ReactElement => {
  const { access } = useAdminAccess();
  const { token, refresh } = useOperationsRefresh();
  const badges = useNavBadges();
  const [status, setStatus] = useState<'' | PrivacyRequestStatus>('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const readList = useCallback(
    (api: AdminApi) => api.listPrivacyRequests(status === '' ? {} : { status }, page, PER_PAGE),
    [status, page],
  );
  const summary = useAdminRead(readSummary, token);
  const list = useAdminRead(readList, token);

  const denied = access.kind === 'denied' || list.state.kind === 'forbidden';
  const canWrite = !denied && access.kind === 'granted' && roleSatisfies(access.role, 'admin');
  const readOnly = !denied && access.kind === 'granted' && !canWrite;

  const reloadAll = (): void => {
    refresh();
    badges.refresh('gdpr');
  };

  const done = (message: string): void => {
    setDialog(null);
    setNotice(message);
    reloadAll();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        eyebrow="Conformité"
        title="Demandes RGPD"
        subtitle="Accès, export, rectification et effacement. Le délai légal de réponse est d’un mois ; l’échéance est calculée à la réception."
        actions={
          <>
            <Button icon={RefreshCw} onClick={reloadAll}>
              Actualiser
            </Button>
            {canWrite ? (
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => {
                  setDialog({ kind: 'new' });
                }}
              >
                Nouvelle demande
              </Button>
            ) : null}
          </>
        }
      />

      {denied ? (
        <AccessDenied title="Votre compte n’a pas accès à cet écran">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration. Le rôle support suffit pour lire le registre.
        </AccessDenied>
      ) : (
        <>
          <InfoNote title="Un registre tenu à la main">
            Le registre ne reçoit pas les demandes tout seul : il garde la trace de celles que vous y saisissez (courriel,
            courrier), calcule leur échéance et mesure les délais. Il ne remplace pas le processus de réponse — l’accès,
            l’export, la rectification et l’anonymisation se font depuis la fiche du compte.
          </InfoNote>

          {readOnly ? (
            <InfoNote title="Lecture seule">
              Votre rôle (support) lit le registre ; enregistrer ou faire évoluer une demande, et télécharger un export,
              demandent le rôle administrateur.
            </InfoNote>
          ) : null}

          {notice === null ? null : (
            <Typography role="status" sx={{ fontSize: 14, color: 'tokens.successText', fontWeight: 700 }}>
              {notice}
            </Typography>
          )}

          <ReadBlock state={summary.state} label="Effectifs du registre" onRetry={summary.reload} skeletonHeight={120}>
            {(data) => (
              <>
                <SummaryTiles summary={data} />
                <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
                  Effacements exécutés sur-le-champ par l’application, sur 90 jours : {String(data.automaticErasures90d)}. Ils
                  n’entrent pas dans ce registre.
                </Typography>
              </>
            )}
          </ReadBlock>

          <Box
            sx={{
              display: 'grid',
              // Sept colonnes ne tiennent pas dans la moitie d'un ecran de 1440 px : le tableau
              // prend toute la largeur, et les deux cartes passent dessous, cote a cote. La
              // disposition de la maquette (cartes a droite) ne revient qu'a partir de 1536 px.
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'minmax(0, 1fr) 380px' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Card
              title="Demandes"
              subtitle="Les demandes ouvertes d’abord, par échéance ; puis les clôturées."
              actions={
                <Box sx={{ minWidth: 180 }}>
                  <SelectField
                    label="Statut"
                    value={status === '' ? ALL : status}
                    onChange={(value) => {
                      setStatus(value === ALL ? '' : (value as PrivacyRequestStatus));
                      setPage(1);
                    }}
                    options={STATUS_FILTER_OPTIONS}
                  />
                </Box>
              }
            >
              <ReadBlock state={list.state} label="Demandes" onRetry={list.reload} skeletonHeight={220}>
                {(data) =>
                  data.total === 0 && status === '' ? (
                    <EmptyState
                      title="Aucune demande enregistrée"
                      description="Le registre démarre vide : aucun historique n’est reconstitué. Les demandes reçues avant lui n’y figurent pas ; saisissez celles reçues par courriel ou par courrier."
                    />
                  ) : (
                    <>
                      <RequestsTable
                        items={data.items}
                        canWrite={canWrite}
                        onEdit={(request) => {
                          setDialog({ kind: 'edit', request });
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
                          {pageCaption(data.total, data.page, data.perPage)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="S"
                            disabled={page <= 1}
                            onClick={() => {
                              setPage((previous) => Math.max(1, previous - 1));
                            }}
                          >
                            Page précédente
                          </Button>
                          <Button
                            size="S"
                            disabled={page * PER_PAGE >= data.total}
                            onClick={() => {
                              setPage((previous) => previous + 1);
                            }}
                          >
                            Page suivante
                          </Button>
                        </Box>
                      </Box>
                    </>
                  )
                }
              </ReadBlock>
            </Card>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: 'minmax(0, 1fr)' },
                gap: 2,
                alignItems: 'start',
                minWidth: 0,
              }}
            >
              <AccessRegistryCard refreshToken={token} />
              <ExportsCard refreshToken={token} canDownload={canWrite} />
            </Box>
          </Box>
        </>
      )}

      {dialog?.kind === 'new' ? (
        <NewRequestDialog
          onClose={() => {
            setDialog(null);
          }}
          onCreated={(created) => {
            setStatus('');
            setPage(1);
            done(`Demande enregistrée. Échéance le ${formatDay(created.dueAt)}.`);
          }}
        />
      ) : null}
      {dialog?.kind === 'edit' ? (
        <EditRequestDialog
          request={dialog.request}
          onClose={() => {
            setDialog(null);
          }}
          onSaved={(updated) => {
            done(updated.status === 'closed' ? 'Demande clôturée.' : 'Demande mise à jour.');
          }}
        />
      ) : null}
    </Box>
  );
};
